import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Shield, Ban, Globe, Plus, Trash2, Users, ShieldCheck, ShieldOff, Key, Download, Upload, Database, Code, Copy, RefreshCw, Check } from 'lucide-react';
import { toast } from 'sonner';

const ALL_PERMS = ['view_elements', 'edit_elements', 'view_prompts', 'edit_prompts', 'create_proposals'];
const PERM_LABELS = {
  view_elements: 'View Elements', edit_elements: 'Edit Elements',
  view_prompts: 'View Prompts', edit_prompts: 'Edit Prompts',
  create_proposals: 'Create Proposals',
};
const PRIMARY_ADMIN = 'hrituraj@triomics.com';

export default function AdminPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newDomain, setNewDomain] = useState('');
  const [tab, setTab] = useState('users');

  const fetchData = useCallback(async () => {
    try {
      const [uRes, dRes] = await Promise.all([api.get('/admin/users'), api.get('/admin/domains')]);
      setUsers(uRes.data || []);
      setDomains(dRes.data || []);
    } catch (err) {
      if (err.response?.status === 403) toast.error('Admin access required');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleBan = async (userId) => {
    try {
      const res = await api.put(`/admin/users/${userId}/ban`);
      setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, banned: res.data.banned } : u));
      toast.success(res.data.banned ? 'User banned' : 'User unbanned');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const toggleRole = async (userId) => {
    try {
      const res = await api.put(`/admin/users/${userId}/role`);
      setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, role: res.data.role } : u));
      toast.success(res.data.role === 'admin' ? 'Promoted to admin' : 'Demoted to user');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const togglePerm = async (userId, perm, currentPerms) => {
    const newPerms = currentPerms.includes(perm) ? currentPerms.filter(p => p !== perm) : [...currentPerms, perm];
    try {
      await api.put(`/admin/users/${userId}/permissions`, { permissions: newPerms });
      setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, permissions: newPerms } : u));
      toast.success('Permissions updated');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const toggleApproval = async (userId) => {
    try {
      const res = await api.put(`/admin/users/${userId}/approve`);
      setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, approved: res.data.approved } : u));
      toast.success(res.data.approved ? 'User approved' : 'User approval revoked');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const addDomain = async () => {
    if (!newDomain.trim()) return;
    try {
      await api.post('/admin/domains', { domain: newDomain.trim().toLowerCase() });
      setNewDomain('');
      toast.success(`Domain @${newDomain.trim()} approved`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const removeDomain = async (domain) => {
    try {
      await api.delete(`/admin/domains/${domain}`);
      setDomains(prev => prev.filter(d => d.domain !== domain));
      toast.success(`Domain @${domain} removed`);
    } catch (err) { toast.error('Failed'); }
  };

  if (user?.role !== 'admin') {
    return (<div className="min-h-screen"><Navbar /><div className="flex items-center justify-center h-64 text-[#9CA3AF] text-sm" data-testid="admin-denied"><Shield className="w-5 h-5 mr-2" /> Admin access required</div></div>);
  }
  if (loading) {
    return (<div className="min-h-screen"><Navbar /><div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin" /></div></div>);
  }

  return (
    <div className="min-h-screen bg-white" data-testid="admin-page">
      <Navbar />
      <div className="px-6 py-5 max-w-6xl">
        <div className="flex items-center gap-3 mb-5">
          <Shield className="w-5 h-5 text-[#002FA7]" />
          <h1 className="text-2xl font-bold tracking-tight text-[#0A0A0A]" style={{ fontFamily: 'Chivo, sans-serif' }}>Admin Panel</h1>
        </div>

        <div className="flex gap-1 mb-5 border-b border-[#E5E7EB]">
          {[
            { key: 'users', label: 'Users', icon: Users, count: users.length },
            { key: 'permissions', label: 'Permissions', icon: Key, count: null },
            { key: 'domains', label: 'Domains', icon: Globe, count: domains.length },
            { key: 'api', label: 'API Keys', icon: Code, count: null },
            { key: 'swagger', label: 'API Docs', icon: Database, count: null },
            { key: 'data', label: 'Data', icon: Database, count: null },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider border-b-2 transition-colors ${tab === t.key ? 'border-[#002FA7] text-[#002FA7]' : 'border-transparent text-[#9CA3AF] hover:text-[#4B5563]'}`} data-testid={`admin-tab-${t.key}`}>
              <t.icon className="w-3.5 h-3.5 inline mr-1.5" />{t.label}{t.count !== null ? ` (${t.count})` : ''}
            </button>
          ))}
        </div>

        {/* Users Tab */}
        {tab === 'users' && (
          <div className="border border-[#E5E7EB]" data-testid="admin-users-table">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F9FAFB] hover:bg-[#F9FAFB]">
                  <TableHead className="text-[10px] font-semibold uppercase tracking-wider w-[40px]"></TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase tracking-wider">Name</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase tracking-wider">Email</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase tracking-wider w-[70px]">Auth</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase tracking-wider w-[70px]">Role</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase tracking-wider w-[70px]">Status</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase tracking-wider w-[100px]">Last Login</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase tracking-wider w-[150px] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map(u => {
                  const isPrimary = u.email === PRIMARY_ADMIN;
                  return (
                    <TableRow key={u.user_id} className={u.banned ? 'bg-red-50/50' : ''} data-testid={`admin-user-${u.user_id}`}>
                      <TableCell>
                        <Avatar className="h-7 w-7"><AvatarImage src={u.picture} /><AvatarFallback className="text-[9px] font-semibold bg-zinc-100">{u.name?.[0]?.toUpperCase()}</AvatarFallback></Avatar>
                      </TableCell>
                      <TableCell className="text-sm font-medium text-[#0A0A0A]">{u.name}</TableCell>
                      <TableCell className="text-xs text-[#4B5563]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{u.email}</TableCell>
                      <TableCell>
                        <span className={`text-[9px] px-1.5 py-0.5 font-medium ${u.auth_method === 'email_password' ? 'bg-blue-50 text-blue-700' : 'bg-zinc-100 text-zinc-600'}`}>
                          {u.auth_method === 'email_password' ? 'Email' : 'SSO'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge className={`text-[10px] px-1.5 py-0 rounded-none font-semibold ${u.role === 'admin' ? 'bg-purple-50 text-purple-700 border border-purple-200' : 'bg-zinc-100 text-zinc-600 border border-zinc-200'}`}>{u.role || 'user'}</Badge>
                      </TableCell>
                      <TableCell>
                        {u.banned ? <Badge className="text-[10px] px-1.5 py-0 rounded-none bg-red-50 text-red-700 border border-red-200">Banned</Badge>
                         : u.approved === false ? <Badge className="text-[10px] px-1.5 py-0 rounded-none bg-amber-50 text-amber-700 border border-amber-200">Pending</Badge>
                         : <Badge className="text-[10px] px-1.5 py-0 rounded-none bg-emerald-50 text-emerald-700 border border-emerald-200">Approved</Badge>}
                      </TableCell>
                      <TableCell className="text-[11px] text-[#9CA3AF]">{u.last_login ? new Date(u.last_login).toLocaleDateString() : '—'}</TableCell>
                      <TableCell className="text-right">
                        {!isPrimary ? (
                          <div className="flex items-center justify-end gap-1">
                            {u.approved === false && (
                              <Button variant="outline" size="sm" className="h-6 text-[11px] rounded-none text-emerald-600 border-emerald-300" onClick={() => toggleApproval(u.user_id)} data-testid={`approve-user-${u.user_id}`}>
                                <ShieldCheck className="w-3 h-3 mr-1" />Approve
                              </Button>
                            )}
                            {u.approved !== false && (
                              <Button variant="ghost" size="sm" className={`h-6 text-[11px] rounded-none ${u.role === 'admin' ? 'text-[#4B5563]' : 'text-purple-600'}`} onClick={() => toggleRole(u.user_id)} data-testid={`role-user-${u.user_id}`}>
                                {u.role === 'admin' ? <><ShieldOff className="w-3 h-3 mr-1" />Demote</> : <><ShieldCheck className="w-3 h-3 mr-1" />Promote</>}
                              </Button>
                            )}
                            <Button variant={u.banned ? 'outline' : 'ghost'} size="sm" className={`h-6 text-[11px] rounded-none ${u.banned ? 'text-emerald-600' : 'text-red-600'}`} onClick={() => toggleBan(u.user_id)} data-testid={`ban-user-${u.user_id}`}>
                              <Ban className="w-3 h-3 mr-1" />{u.banned ? 'Unban' : 'Ban'}
                            </Button>
                          </div>
                        ) : <span className="text-[10px] text-[#9CA3AF] italic">Primary admin</span>}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Permissions Tab */}
        {tab === 'permissions' && (
          <div>
            <div className="bg-[#F9FAFB] border border-[#E5E7EB] p-4 mb-4">
              <p className="text-xs text-[#4B5563]">
                <strong>Granular Permissions:</strong> Control what each user can do. Admin users always have all permissions.
              </p>
            </div>
            <div className="border border-[#E5E7EB]" data-testid="permissions-table">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F9FAFB] hover:bg-[#F9FAFB]">
                    <TableHead className="text-[10px] font-semibold uppercase tracking-wider">User</TableHead>
                    <TableHead className="text-[10px] font-semibold uppercase tracking-wider w-[70px]">Role</TableHead>
                    {ALL_PERMS.map(p => (
                      <TableHead key={p} className="text-[10px] font-semibold uppercase tracking-wider text-center w-[100px]">
                        {PERM_LABELS[p]}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map(u => {
                    const isPrimary = u.email === PRIMARY_ADMIN;
                    const isAdmin = u.role === 'admin';
                    const perms = u.permissions || [];
                    return (
                      <TableRow key={u.user_id} data-testid={`perm-row-${u.user_id}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Avatar className="h-6 w-6"><AvatarFallback className="text-[9px] bg-zinc-100">{u.name?.[0]}</AvatarFallback></Avatar>
                            <div>
                              <div className="text-xs font-medium text-[#0A0A0A]">{u.name}</div>
                              <div className="text-[10px] text-[#9CA3AF]">{u.email}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={`text-[9px] px-1 py-0 rounded-none ${isAdmin ? 'bg-purple-50 text-purple-700' : 'bg-zinc-100 text-zinc-600'}`}>{u.role}</Badge>
                        </TableCell>
                        {ALL_PERMS.map(p => (
                          <TableCell key={p} className="text-center">
                            {isAdmin || isPrimary ? (
                              <Checkbox checked={true} disabled className="h-4 w-4 rounded-none opacity-50" />
                            ) : (
                              <Checkbox
                                checked={perms.includes(p)}
                                onCheckedChange={() => togglePerm(u.user_id, p, perms)}
                                className="h-4 w-4 rounded-none"
                                data-testid={`perm-${u.user_id}-${p}`}
                              />
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {/* Domains Tab */}
        {tab === 'domains' && (
          <div>
            <div className="bg-[#F9FAFB] border border-[#E5E7EB] p-4 mb-4">
              <p className="text-xs text-[#4B5563]">
                <strong>Domain allowlist:</strong> When domains are added, only users from these domains can register/login. Admin emails bypass this.
              </p>
              <div className="flex gap-2 mt-3">
                <Input data-testid="domain-input" placeholder="e.g. triomics.com" value={newDomain} onChange={e => setNewDomain(e.target.value)} onKeyDown={e => e.key === 'Enter' && addDomain()} className="h-8 text-xs rounded-none max-w-xs" />
                <Button onClick={addDomain} disabled={!newDomain.trim()} className="h-8 text-xs rounded-none" data-testid="add-domain-btn"><Plus className="w-3 h-3 mr-1" /> Add</Button>
              </div>
            </div>
            {domains.length > 0 ? (
              <div className="border border-[#E5E7EB]" data-testid="domains-table">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F9FAFB]">
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider">Domain</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider w-[100px]">Added</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider w-[80px] text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {domains.map(d => (
                      <TableRow key={d.domain} data-testid={`domain-row-${d.domain}`}>
                        <TableCell className="text-sm text-[#0A0A0A]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>@{d.domain}</TableCell>
                        <TableCell className="text-[11px] text-[#9CA3AF]">{d.created_at ? new Date(d.created_at).toLocaleDateString() : '—'}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" className="h-6 text-[11px] rounded-none text-red-600" onClick={() => removeDomain(d.domain)} data-testid={`remove-domain-${d.domain}`}><Trash2 className="w-3 h-3 mr-1" /> Remove</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="border border-dashed border-[#E5E7EB] p-8 text-center">
                <Globe className="w-8 h-8 text-[#9CA3AF] mx-auto mb-2" />
                <p className="text-sm text-[#4B5563]">No domains configured</p>
                <p className="text-xs text-[#9CA3AF] mt-1">All accounts can register/login</p>
              </div>
            )}
          </div>
        )}

        {/* Data Tab */}
        {tab === 'data' && (
          <DataTab />
        )}

        {/* API Keys Tab */}
        {tab === 'api' && (
          <ApiKeysTab />
        )}

        {/* Swagger Tab */}
        {tab === 'swagger' && (
          <SwaggerTab />
        )}
      </div>
    </div>
  );
}

function DataTab() {
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileRef = useState(null);

  const handleExport = async () => {
    setExporting(true);
    try {
      const r = await api.get('/admin/data/export', { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `triomics_export_${new Date().toISOString().slice(0,10)}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch { toast.error('Export failed'); }
    finally { setExporting(false); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!window.confirm('This will REPLACE all existing dictionary data. Continue?')) {
      e.target.value = '';
      return;
    }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/admin/data/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Import successful: ${JSON.stringify(r.data.counts)}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Import failed'); }
    finally { setImporting(false); e.target.value = ''; }
  };

  return (
    <div>
      <div className="bg-[#F9FAFB] border border-[#E5E7EB] p-4 mb-4">
        <p className="text-xs text-[#4B5563]"><strong>Data Export/Import:</strong> Export all schemas, entities, attributes, and valuesets as a ZIP file. Import replaces ALL existing dictionary data.</p>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="border border-[#E5E7EB] p-6 text-center">
          <Download className="w-8 h-8 text-[#002FA7] mx-auto mb-3" />
          <h3 className="text-sm font-semibold text-[#0A0A0A] mb-1">Export Dictionary</h3>
          <p className="text-xs text-[#9CA3AF] mb-4">Download all schemas, entities, attributes, and valuesets as a ZIP file</p>
          <Button onClick={handleExport} disabled={exporting} className="rounded-none bg-[#002FA7] hover:bg-[#002585]" data-testid="export-data-btn">
            <Download className="w-3.5 h-3.5 mr-1.5" /> {exporting ? 'Exporting...' : 'Export ZIP'}
          </Button>
        </div>
        <div className="border border-[#E5E7EB] p-6 text-center">
          <Upload className="w-8 h-8 text-amber-600 mx-auto mb-3" />
          <h3 className="text-sm font-semibold text-[#0A0A0A] mb-1">Import Dictionary</h3>
          <p className="text-xs text-[#9CA3AF] mb-4">Upload a ZIP file to replace all existing dictionary data</p>
          <label className="inline-flex items-center gap-1.5 px-4 py-2 bg-amber-50 border border-amber-200 text-amber-700 text-sm font-medium cursor-pointer hover:bg-amber-100 transition-colors" data-testid="import-data-btn">
            <Upload className="w-3.5 h-3.5" /> {importing ? 'Importing...' : 'Import ZIP'}
            <input type="file" accept=".zip" onChange={handleImport} className="hidden" disabled={importing} />
          </label>
        </div>
      </div>
    </div>
  );
}


function ApiKeysTab() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [copiedKey, setCopiedKey] = useState(null);

  const fetchClients = async () => {
    try { const r = await api.get('/admin/api-clients'); setClients(r.data); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchClients(); }, []);

  const handleCreate = async () => {
    if (!name.trim()) return;
    try {
      const r = await api.post('/admin/api-clients', { name: name.trim(), description: desc.trim() });
      toast.success(`Client created. API Key: ${r.data.api_key}`);
      setCreateOpen(false);
      setName(''); setDesc('');
      fetchClients();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const toggleClient = async (cid) => {
    try { const r = await api.put(`/admin/api-clients/${cid}/toggle`); setClients(prev => prev.map(c => c.client_id === cid ? { ...c, active: r.data.active } : c)); }
    catch { toast.error('Failed'); }
  };

  const deleteClient = async (cid) => {
    if (!window.confirm('Delete this API client?')) return;
    try { await api.delete(`/admin/api-clients/${cid}`); toast.success('Deleted'); fetchClients(); }
    catch { toast.error('Failed'); }
  };

  const regenerateKey = async (cid) => {
    if (!window.confirm('Regenerate API key? The old key will stop working immediately.')) return;
    try { const r = await api.post(`/admin/api-clients/${cid}/regenerate`); toast.success(`New key: ${r.data.api_key}`); fetchClients(); }
    catch { toast.error('Failed'); }
  };

  const copyKey = (key) => {
    navigator.clipboard.writeText(key);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  if (loading) return <div className="flex justify-center py-8"><div className="w-5 h-5 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin" /></div>;

  return (
    <div>
      <div className="bg-[#F9FAFB] border border-[#E5E7EB] p-4 mb-4">
        <p className="text-xs text-[#4B5563]"><strong>API Keys:</strong> Create API keys for external clients to access the public API (GET /api/v1/...). Keys authenticate via the <code className="bg-zinc-200 px-1">X-API-Key</code> header.</p>
      </div>

      <div className="flex justify-end mb-3">
        <Button onClick={() => setCreateOpen(true)} className="rounded-none bg-[#0A0A0A] text-white hover:bg-[#171717] h-8 text-xs" data-testid="create-api-key-btn">
          <Plus className="w-3.5 h-3.5 mr-1.5" /> New API Client
        </Button>
      </div>

      {clients.length === 0 ? (
        <div className="border border-dashed border-[#E5E7EB] p-8 text-center"><Code className="w-8 h-8 text-[#9CA3AF] mx-auto mb-2" /><p className="text-sm text-[#4B5563]">No API clients created</p></div>
      ) : (
        <div className="border border-[#E5E7EB]" data-testid="api-clients-table">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F9FAFB]">
                <TableHead className="text-[10px] font-semibold uppercase tracking-wider">Name</TableHead>
                <TableHead className="text-[10px] font-semibold uppercase tracking-wider">Client ID</TableHead>
                <TableHead className="text-[10px] font-semibold uppercase tracking-wider">API Key</TableHead>
                <TableHead className="text-[10px] font-semibold uppercase tracking-wider w-[60px]">Status</TableHead>
                <TableHead className="text-[10px] font-semibold uppercase tracking-wider w-[60px]">Requests</TableHead>
                <TableHead className="text-[10px] font-semibold uppercase tracking-wider w-[80px]">Last Used</TableHead>
                <TableHead className="text-[10px] font-semibold uppercase tracking-wider w-[140px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {clients.map(c => (
                <TableRow key={c.client_id} data-testid={`client-${c.client_id}`}>
                  <TableCell><div className="text-sm font-medium text-[#0A0A0A]">{c.name}</div>{c.description && <div className="text-[10px] text-[#9CA3AF]">{c.description}</div>}</TableCell>
                  <TableCell><span className="text-xs text-[#4B5563]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{c.client_id}</span></TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-[#4B5563] font-mono">{c.api_key?.slice(0, 12)}...{c.api_key?.slice(-4)}</span>
                      <button onClick={() => copyKey(c.api_key)} className="text-[#9CA3AF] hover:text-[#4B5563]">
                        {copiedKey === c.api_key ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
                      </button>
                    </div>
                  </TableCell>
                  <TableCell><Badge className={`text-[10px] px-1.5 py-0 rounded-none ${c.active ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>{c.active ? 'Active' : 'Revoked'}</Badge></TableCell>
                  <TableCell className="text-xs text-[#4B5563]">{c.request_count || 0}</TableCell>
                  <TableCell className="text-[11px] text-[#9CA3AF]">{c.last_used ? new Date(c.last_used).toLocaleDateString() : '—'}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" className="h-6 text-[10px] rounded-none" onClick={() => regenerateKey(c.client_id)}><RefreshCw className="w-3 h-3 mr-1" />Regen</Button>
                      <Button variant="ghost" size="sm" className={`h-6 text-[10px] rounded-none ${c.active ? 'text-amber-600' : 'text-emerald-600'}`} onClick={() => toggleClient(c.client_id)}>{c.active ? 'Revoke' : 'Activate'}</Button>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0 rounded-none text-red-500" onClick={() => deleteClient(c.client_id)}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="rounded-none max-w-sm" data-testid="create-client-dialog">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>New API Client</DialogTitle><DialogDescription className="sr-only">Create API client</DialogDescription></DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label className="text-xs font-semibold text-[#4B5563]">Client Name</Label><Input value={name} onChange={e => setName(e.target.value)} className="rounded-none mt-1" placeholder="e.g., Data Pipeline" data-testid="client-name-input" /></div>
            <div><Label className="text-xs font-semibold text-[#4B5563]">Description</Label><Input value={desc} onChange={e => setDesc(e.target.value)} className="rounded-none mt-1" placeholder="optional" /></div>
            <Button onClick={handleCreate} disabled={!name.trim()} className="w-full rounded-none bg-[#002FA7] hover:bg-[#002585]" data-testid="save-client-btn"><Plus className="w-3.5 h-3.5 mr-1.5" /> Create Client</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SwaggerTab() {
  const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
  return (
    <div data-testid="swagger-tab">
      <div className="bg-[#F9FAFB] border border-[#E5E7EB] p-4 mb-4">
        <p className="text-xs text-[#4B5563]"><strong>API Documentation:</strong> Interactive Swagger/OpenAPI documentation for the public API. Use your API key in the <code className="bg-zinc-200 px-1">X-API-Key</code> header to authenticate requests.</p>
        <p className="text-xs text-[#9CA3AF] mt-1">Base URL: <code className="bg-zinc-200 px-1">{baseUrl}/api/v1</code></p>
      </div>
      <div className="border border-[#E5E7EB]">
        <iframe
          src={`${baseUrl}/api/public/docs`}
          title="API Documentation"
          className="w-full border-0"
          style={{ height: 'calc(100vh - 280px)', minHeight: '500px' }}
          data-testid="swagger-iframe"
        />
      </div>
    </div>
  );
}
